/**
 * Simple Éclair — Job Queue Admin (T9)
 * 
 * Vue consolidée de la file d'attente de jobs :
 * - Santé globale de la queue (healthy / warning / critical)
 * - Compteurs temps réel par statut
 * - Jobs actifs avec logs en direct
 * - Historique des échecs récents
 * - Métriques : temps moyen, retries, throughput
 */
import { useEffect, useState, useCallback } from 'react';
import { Loader2, RefreshCw, Activity, AlertTriangle, CheckCircle2, XCircle, Clock, Zap } from 'lucide-react';

import { API_URL, ADMIN_KEY } from '../lib/config';

// ==========================================
// Types
// ==========================================
interface QueueStats {
  health: 'healthy' | 'warning' | 'critical';
  counters: {
    queued: number;
    in_progress: number;
    review: number;
    done: number;
    failed: number;
    total_7d: number;
    done_today: number;
  };
  by_type: Record<string, number>;
  avg_processing_ms: number;
  total_retries_7d: number;
  recent_failures: Array<{
    id: string;
    type: string;
    error: string;
    created: string;
    retry_count: number;
  }>;
  active_chains: number;
}

interface ActiveJob {
  id: string;
  type: string;
  status: string;
  description: string;
  business_name: string;
  started_at: string;
  error: string;
  retry_count: number;
  priority: number;
}

interface LogEntry {
  ts: string;
  level: string;
  msg: string;
}

// ==========================================
// Utilitaires
// ==========================================

/** Formatage durée en texte lisible */
function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}min ${s % 60}s`;
  return `${Math.floor(m / 60)}h${(m % 60).toString().padStart(2, '0')}`;
}

/** Temps écoulé depuis une date */
function timeAgo(dateStr: string): string {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'à l\'instant';
  if (min < 60) return `il y a ${min}min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `il y a ${h}h`;
  return `il y a ${Math.floor(h / 24)}j`;
}

const JOB_TYPE_ICONS: Record<string, string> = {
  rebuild: '🔄',
  addon_logo: '🎨',
  addon_blog: '📝',
  addon_form: '📋',
  addon_gmb_create: '📍',
  addon_gmb_optimize: '🔧',
  addon_domain: '🌐',
  rejection_fix: '❌→✅',
  visibility_action: '📈',
};

// ==========================================
// Composant : Indicateur de santé
// ==========================================
function HealthIndicator({ health }: { health: string }) {
  const config: Record<string, { icon: any; label: string; color: string; bg: string; ring: string }> = {
    healthy: { icon: CheckCircle2, label: 'Système opérationnel', color: 'text-green-700', bg: 'bg-green-50 border-green-200', ring: 'ring-green-400' },
    warning: { icon: AlertTriangle, label: 'Attention requise', color: 'text-amber-700', bg: 'bg-amber-50 border-amber-200', ring: 'ring-amber-400' },
    critical: { icon: XCircle, label: 'Problème critique', color: 'text-red-700', bg: 'bg-red-50 border-red-200', ring: 'ring-red-400' },
  };
  const c = config[health] || config.healthy;
  const Icon = c.icon;

  return (
    <div className={`flex items-center gap-3 px-5 py-4 rounded-xl border-2 ${c.bg} ${c.ring}`}>
      <div className={`p-2 rounded-full ${health === 'healthy' ? 'bg-green-100' : health === 'warning' ? 'bg-amber-100' : 'bg-red-100'}`}>
        <Icon className={`w-6 h-6 ${c.color}`} />
      </div>
      <div>
        <p className={`font-semibold ${c.color}`}>{c.label}</p>
        <p className="text-xs text-gray-500 mt-0.5">
          {health === 'healthy' ? 'Tous les jobs s\'exécutent normalement' :
           health === 'warning' ? 'Des échecs récents nécessitent attention' :
           'Plusieurs échecs ou queue saturée — vérifier immédiatement'}
        </p>
      </div>
    </div>
  );
}

// ==========================================
// Composant : Logs viewer inline
// ==========================================
function InlineLogsViewer({ jobId }: { jobId: string }) {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_URL}/admin/jobs/${jobId}/logs`, {
      headers: { 'Authorization': `Bearer ${ADMIN_KEY}` },
    })
      .then(r => r.json())
      .then((data: any) => setLogs(data.logs || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [jobId]);

  if (loading) return <div className="text-xs text-gray-400 py-2">Chargement...</div>;
  if (logs.length === 0) return <div className="text-xs text-gray-400 italic py-1">Pas de logs</div>;

  return (
    <div className="bg-gray-900 rounded-lg p-3 max-h-48 overflow-y-auto font-mono text-[11px] leading-5 mt-2">
      {logs.slice(-20).map((entry, i) => {
        const ts = entry.ts?.substring(11, 19) || '';
        const color = entry.level === 'error' ? 'text-red-400' : entry.level === 'warn' ? 'text-amber-400' : 'text-gray-300';
        return (
          <div key={i} className={`${color} hover:bg-gray-800/50 px-1 rounded`}>
            <span className="text-gray-500">{ts}</span> {entry.msg}
          </div>
        );
      })}
    </div>
  );
}

// ==========================================
// Page principale : Job Queue Admin
// ==========================================
const PAGE_SIZE = 10;

export default function JobQueue() {
  const [stats, setStats] = useState<QueueStats | null>(null);
  const [activeJobs, setActiveJobs] = useState<ActiveJob[]>([]);
  const [totalActiveJobs, setTotalActiveJobs] = useState(0);
  const [totalActivePages, setTotalActivePages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedJob, setExpandedJob] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  const fetchActiveJobs = useCallback(async (page = 1, append = false) => {
    if (append) setLoadingMore(true);
    const jobsRes = await fetch(`${API_URL}/admin/jobs?status=queued,in_progress,review&limit=${PAGE_SIZE}&page=${page}`, {
      headers: { 'Authorization': `Bearer ${ADMIN_KEY}` },
    });
    if (!jobsRes.ok) throw new Error(`Jobs: HTTP ${jobsRes.status}`);
    const jobsData = await jobsRes.json() as any;
    const newItems = (jobsData.items || []).map((j: any) => ({
      id: j.id,
      type: j.type,
      status: j.status,
      description: j.description,
      business_name: j.business_name || j.expand?.submission_id?.business_name || j.submission_id,
      started_at: j.started_at,
      error: j.error,
      retry_count: j.retry_count || 0,
      priority: j.priority || 0,
    }));
    if (append) {
      setActiveJobs(prev => [...prev, ...newItems]);
    } else {
      setActiveJobs(newItems);
    }
    setTotalActiveJobs(jobsData.totalItems || 0);
    setTotalActivePages(jobsData.totalPages || 1);
    setCurrentPage(jobsData.page || page);
    if (append) setLoadingMore(false);
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const statsRes = await fetch(`${API_URL}/admin/queue-stats`, {
        headers: { 'Authorization': `Bearer ${ADMIN_KEY}` },
      });
      if (!statsRes.ok) throw new Error(`Stats: HTTP ${statsRes.status}`);
      const statsData = await statsRes.json() as QueueStats;
      setStats(statsData);

      await fetchActiveJobs(1, false);
      setError(null);
      setLastRefresh(new Date());
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [fetchActiveJobs]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-refresh toutes les 10s
  useEffect(() => {
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleLoadMore = () => {
    if (currentPage < totalActivePages) {
      fetchActiveJobs(currentPage + 1, true);
    }
  };

  const hasMore = currentPage < totalActivePages;

  // Action : relancer un job échoué
  const handleRetry = async (jobId: string) => {
    try {
      await fetch(`${API_URL}/admin/jobs/${jobId}`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${ADMIN_KEY}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'queued', error: '', started_at: '', completed_at: '' }),
      });
      fetchData();
    } catch {}
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-100px)]">
        <div className="flex items-center gap-3 text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span className="text-sm">Chargement de la queue...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">⚙️ Job Queue</h1>
          <p className="text-gray-500 mt-1">File d'attente — monitoring et logs en temps réel</p>
        </div>
        <button
          onClick={fetchData}
          className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          <span className="text-xs text-gray-400">{lastRefresh.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span>
        </button>
      </div>

      {error && (
        <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg text-sm mb-4">
          Erreur: {error}
        </div>
      )}

      {/* Santé globale */}
      {stats && (
        <>
          <div className="mb-6">
            <HealthIndicator health={stats.health} />
          </div>

          {/* Compteurs principaux */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-50 rounded-lg">
                  <Clock className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">{stats.counters.queued}</div>
                  <div className="text-xs text-gray-500">En attente</div>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-50 rounded-lg">
                  <Activity className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">{stats.counters.in_progress}</div>
                  <div className="text-xs text-gray-500">En cours</div>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-50 rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">{stats.counters.done_today}</div>
                  <div className="text-xs text-gray-500">Terminés aujourd'hui</div>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-50 rounded-lg">
                  <Zap className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">{formatDuration(stats.avg_processing_ms)}</div>
                  <div className="text-xs text-gray-500">Temps moyen</div>
                </div>
              </div>
            </div>
          </div>

          {/* Métriques secondaires */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-gray-50 rounded-xl border border-gray-200 px-4 py-3 flex items-center justify-between">
              <span className="text-sm text-gray-600">Jobs 7 derniers jours</span>
              <span className="font-bold text-gray-900">{stats.counters.total_7d}</span>
            </div>
            <div className="bg-gray-50 rounded-xl border border-gray-200 px-4 py-3 flex items-center justify-between">
              <span className="text-sm text-gray-600">Retries (7j)</span>
              <span className="font-bold text-orange-600">{stats.total_retries_7d}</span>
            </div>
            <div className="bg-gray-50 rounded-xl border border-gray-200 px-4 py-3 flex items-center justify-between">
              <span className="text-sm text-gray-600">Chaînes actives</span>
              <span className="font-bold text-blue-600">{stats.active_chains}</span>
            </div>
          </div>

          {/* Répartition par type */}
          {Object.keys(stats.by_type).length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
              <h3 className="font-semibold text-gray-900 text-sm mb-3">Répartition par type (7j)</h3>
              <div className="flex flex-wrap gap-2">
                {Object.entries(stats.by_type)
                  .sort(([, a], [, b]) => b - a)
                  .map(([type, count]) => (
                    <div key={type} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
                      <span>{JOB_TYPE_ICONS[type] || '⚙️'}</span>
                      <span className="text-sm text-gray-700">{type}</span>
                      <span className="text-xs font-bold text-gray-900 bg-white px-2 py-0.5 rounded-full border">{count}</span>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Jobs actifs (en cours + en attente + en review) */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-6">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="font-semibold text-gray-900">
            🚦 Jobs actifs
          </h3>
          <span className="text-sm text-gray-500">
            {activeJobs.length} affiché{activeJobs.length > 1 ? 's' : ''} / {totalActiveJobs} total
          </span>
        </div>
        {activeJobs.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            <p>Aucun job actif — la queue est vide 🎉</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {activeJobs.map(job => {
              const icon = JOB_TYPE_ICONS[job.type] || '⚙️';
              const statusColors: Record<string, string> = {
                queued: 'bg-yellow-50 text-yellow-700 border-yellow-200',
                in_progress: 'bg-blue-50 text-blue-700 border-blue-200',
                review: 'bg-purple-50 text-purple-700 border-purple-200',
              };
              const isExpanded = expandedJob === job.id;

              return (
                <div key={job.id} className={`${job.status === 'in_progress' ? 'bg-blue-50/20' : ''}`}>
                  <div
                    className="px-5 py-3 flex items-center gap-4 cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => setExpandedJob(isExpanded ? null : job.id)}
                  >
                    <span className="text-xl flex-shrink-0">{icon}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">{job.business_name}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full border ${statusColors[job.status] || 'bg-gray-50 text-gray-600'}`}>
                          {job.status === 'queued' ? '⏳ En attente' : job.status === 'in_progress' ? '▶️ En cours' : '🔍 À valider'}
                        </span>
                        {job.priority > 0 && (
                          <span className="text-xs text-red-600">🔥 Priorité {job.priority}</span>
                        )}
                        {job.retry_count > 0 && (
                          <span className="text-xs text-orange-600">🔄 Retry {job.retry_count}</span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 truncate mt-0.5">{job.description}</p>
                    </div>
                    <div className="text-xs text-gray-400 flex-shrink-0">
                      {job.started_at ? timeAgo(job.started_at) : ''}
                    </div>
                    <span className="text-gray-400 text-xs">{isExpanded ? '▲' : '▼'}</span>
                  </div>
                  {/* Logs inline quand déplié */}
                  {isExpanded && (
                    <div className="px-5 pb-4">
                      <InlineLogsViewer jobId={job.id} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Bouton "Voir plus" */}
        {hasMore && (
          <div className="border-t border-gray-100 px-5 py-4 text-center">
            <button
              onClick={handleLoadMore}
              disabled={loadingMore}
              className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-[#4ECDC4] bg-[#4ECDC4]/10 hover:bg-[#4ECDC4]/20 rounded-lg transition-colors disabled:opacity-50"
            >
              {loadingMore ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Chargement...
                </>
              ) : (
                <>
                  Voir plus ({totalActiveJobs - activeJobs.length} restants)
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Derniers échecs */}
      {stats && stats.recent_failures.length > 0 && (
        <div className="bg-white rounded-xl border border-red-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-red-100 bg-red-50/50">
            <h3 className="font-semibold text-red-800">
              ❌ Échecs récents ({stats.recent_failures.length})
            </h3>
          </div>
          <div className="divide-y divide-gray-100">
            {stats.recent_failures.map(failure => (
              <div key={failure.id} className="px-5 py-3 flex items-center gap-4">
                <span className="text-lg flex-shrink-0">{JOB_TYPE_ICONS[failure.type] || '⚙️'}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900">{failure.type}</span>
                    {failure.retry_count > 0 && (
                      <span className="text-xs text-orange-600">
                        🔄 {failure.retry_count} retries
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-red-600 truncate mt-0.5" title={failure.error}>
                    {failure.error}
                  </p>
                </div>
                <div className="text-xs text-gray-400 flex-shrink-0">
                  {timeAgo(failure.created)}
                </div>
                <button
                  onClick={() => handleRetry(failure.id)}
                  className="text-xs text-blue-600 hover:text-blue-800 px-2 py-1 rounded hover:bg-blue-50 transition-colors flex-shrink-0"
                >
                  🔄 Relancer
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
